import { QuillEditor } from "@pathwright/blocks/src/shared"
import { sansSerifStyles } from "@pathwright/blocks/src/ui/typography"
import Button from "@pathwright/ui/src/components/button/Button"
import IconButton from "@pathwright/ui/src/components/button/IconButton"
import { Content } from "@pathwright/ui/src/components/content/Content"
import PropTypes from "prop-types"
import React from "react"
import { Manager, Popper, Reference } from "react-popper"
import styled from "styled-components"

const StyledWrapper = styled.div`
  textarea {
    outline: none;
    border: none;
    resize: none;
    width: 100%;
  }
`

const AnnotationTarget = styled.div`
  z-index: 5;
  position: absolute;
  width: 20px;
  height: 20px;
  box-shadow: 0 0 3px 3px rgba(0, 0, 0, 0.1);
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 15px;
  transform: translate3d(-50%, -50%, 0);
  outline: none;
  &:focus {
    outline: none;
  }
`

const AnnotationCircle = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 5px;
  background-color: #888888;
`

const TooltipContainer = styled.div`
  z-index: 100;
  min-width: 250px;
  max-width: 50%;
  border-radius: 3px;
  background-color: #fff;
  padding-top: 20px;
  padding-bottom: 15px;
  padding-left: ${(props) => (props.build ? 40 : 20)}px;
  padding-right: ${(props) => (props.build ? 40 : 20)}px;
  box-shadow: 0 0 6px 6px rgba(0, 0, 0, 0.1);
  /* pointer-events: ${(props) => (props.build ? "auto" : "none")}; */
  margin: 0px 6px;
  &.left {
    left: auto !important;
    transform: none !important;
    right: ${(props) => 100 - props.annotation.xPos + "%"} !important;
    top: ${(props) => props.annotation.yPos + "%"} !important;
    margin-right: 15px;
    margin-top: -47px;
  }
  @media (max-width: 768px) {
    & {
      max-width: none;
      margin: 20px 5px 0px !important;
      left: 0 !important;
      right: 0 !important;
      transform: none !important;
      margin-top: 20px !important;
    }
    &.left,
    &.right {
      left: 0 !important;
      right: 0 !important;
    }
    &.right {
      top: ${(props) => props.annotation.yPos + "%"} !important;
    }
    &.bottom {
      bottom: ${(props) => 100 - props.annotation.yPos + "%"};
      top: auto !important;
      margin-bottom: 20px !important;
    }
  }

  .ql-blank:before,
  .ql-editor > p {
    color: rgba(0, 0, 0, 0.6) !important;
    ${sansSerifStyles({ size: 18 })}
  }

  .ql-tooltip.ql-editing[data-mode="link"] .ql-tooltip-editor input {
    left: 0;
  }
`

const TooltipArrow = styled("div")`
  position: absolute;
  width: 10px;
  height: 10px;
  pointer-events: none;
  &[data-placement*="right"] {
    left: 0;
    margin-left: -5px;
    height: 5px;
    width: 5px;
    &::before {
      border-width: 5px 5px 5px 0;
      border-color: transparent #fff transparent transparent;
    }
  }
  &[data-placement*="left"] {
    right: 0;
    margin-right: -5px;
    height: 5px;
    width: 5px;
    top: 40px !important;
    &::before {
      border-width: 5px 0 5px 5px;
      border-color: transparent transparent transparent #fff;
    }
  }
  &::before {
    content: "";
    margin: auto;
    display: block;
    width: 0;
    height: 0;
    border-style: solid;
  }
  @media (max-width: 768px) {
    &.top {
      left: ${(props) => props.annotation.xPos + "%"} !important;
      top: 0px !important;
      margin: -7px 0px 0px !important;
      margin-left: -1% !important;
      &::before {
        border-width: 5px 5px 5px 0px;
        border-color: transparent #fff transparent transparent;
        transform: rotate(90deg);
      }
    }
    &.bottom {
      left: ${(props) => props.annotation.xPos + "%"} !important;
      top: auto !important;
      bottom: 0px !important;
      margin: 0px 0px -5px !important;
      &::before {
        border-width: 5px 5px 5px 5px;
        border-color: #fff transparent transparent transparent;
      }
    }
  }
`

const propsAreEqual = (prevProps, nextProps) => {
  if (prevProps.active !== nextProps.active) return false
  if (prevProps.disabled !== nextProps.disabled) return false
  if (prevProps.build !== nextProps.build) return false
  if (prevProps.annotation !== nextProps.annotation) return false

  return true
}

const Annotation = React.memo((props) => {
  const {
    annotation,
    active,
    disabled,
    build,
    onChangeAnnotation,
    onShowAnnotation,
    onHideAnnotation,
    onRemoveAnnotation
  } = props

  const [isActive, setIsActive] = React.useState(active || false)

  React.useEffect(() => {
    setIsActive(active)
  }, [active])

  const handleFocusTooltip = () => {
    if (disabled && build) return

    setIsActive(true)
    onShowAnnotation()
  }

  const handleBlurTooltip = () => {
    if (build) return

    setIsActive(false)
    onHideAnnotation()
  }

  const handleMouseEnterTooltip = (event) => {
    if (disabled && build) return

    event.target.focus()
  }

  const handleMouseLeaveTooltip = (event) => {
    if (build) return

    handleBlurTooltip()
  }

  const hideTooltip = () => {
    setIsActive(false)
    onHideAnnotation()
  }

  const handleDelete = (e) => {
    e.preventDefault()
    e.stopPropagation()

    onRemoveAnnotation()
  }

  const placementOrientation = annotation.xPos < 50 ? "right" : "left"
  const verticalPlacementOrientation = annotation.yPos < 50 ? "top" : "bottom"

  return (
    <Manager>
      <StyledWrapper className="Annotation">
        <Reference>
          {({ ref }) => (
            <AnnotationTarget
              className="AnnotationTarget"
              ref={ref}
              tabIndex="0"
              disabled={disabled}
              onFocus={handleFocusTooltip}
              onMouseEnter={handleMouseEnterTooltip}
              style={{
                left: annotation.xPos + "%",
                top: annotation.yPos + "%"
              }}
            >
              <AnnotationCircle className="AnnotationCircle" />
            </AnnotationTarget>
          )}
        </Reference>
        {isActive && (
          <Popper
            placement={placementOrientation}
            modifiers={{ preventOveflow: { enabled: true } }}
          >
            {({ ref, style, placement, arrowProps }) => (
              <TooltipContainer
                ref={ref}
                build={build}
                style={{ ...style }}
                className={`AnnotationTooltip ${placementOrientation} ${verticalPlacementOrientation}`}
                onMouseLeave={handleMouseLeaveTooltip}
                annotation={annotation}
              >
                {build && active ? (
                  <div>
                    <QuillEditor
                      className="AnnotationEditor"
                      // autofocus={active}
                      html={annotation.html}
                      onChange={(html) =>
                        onChangeAnnotation({
                          ...annotation,
                          html
                        })
                      }
                    />
                    <Button onClick={hideTooltip}>Done</Button>
                    <IconButton
                      icon="trash"
                      onClick={handleDelete}
                      style={{ position: "absolute", top: 5, right: 5 }}
                    />
                  </div>
                ) : (
                  <Content.Text
                    dangerouslySetInnerHTML={{
                      __html: annotation.html
                    }}
                  />
                )}
                <TooltipArrow
                  ref={arrowProps.ref}
                  data-placement={placement}
                  style={{ ...arrowProps.style }}
                  className={verticalPlacementOrientation}
                  annotation={annotation}
                />
              </TooltipContainer>
            )}
          </Popper>
        )}
      </StyledWrapper>
    </Manager>
  )
}, propsAreEqual)

Annotation.propTypes = {
  annotation: PropTypes.shape({
    html: PropTypes.string.isRequired,
    xPos: PropTypes.number.isRequired,
    yPos: PropTypes.number.isRequired
  }).isRequired,
  onHideAnnotation: PropTypes.func.isRequired,
  onShowAnnotation: PropTypes.func.isRequired,
  onChangeAnnotation: PropTypes.func.isRequired,
  onRemoveAnnotation: PropTypes.func.isRequired
}

export default Annotation
